import React, { useId } from 'react';
import { Avatar } from '@mui/material';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import NorthEastIcon from '@mui/icons-material/NorthEast';
import EmojiEventsOutlinedIcon from '@mui/icons-material/EmojiEventsOutlined';
import { useTranslation } from 'react-i18next';
import { getMatchdayNumber } from '../../utils/matchdays';
import { reportNumber } from '../../utils/rivalReport';

export function ReportMatchdays({ current, rounds = [] }) {
  const { t, i18n } = useTranslation('reports');
  const titleId = useId();
  const total = rounds.length;
  const played = rounds.filter(round => (round.partidos || []).some(match =>
    match.partidoJugado === true && [match.golesCasa, match.golesVisitante]
      .every(value => Number.isInteger(reportNumber(value))))).length;
  const number = getMatchdayNumber({ jornada: current });
  const date = String(current ?? '').match(/\(([^)]+)\)/)?.[1];
  const percent = total ? played / total * 100 : 0;
  return <section className="rr-panel rr-calendar-panel" aria-labelledby={titleId}>
    <header className="rr-performance-heading"><div><span className="rr-eyebrow">{t('modern.context.competition')}</span>
      <h2 id={titleId}>{t('sections.matchdays')}</h2></div><EventNoteOutlinedIcon aria-hidden="true" /></header>
    <dl className="rr-calendar-metrics">
      <div className="rr-calendar-current"><dt>{t('stats.currentMatchday')}</dt>
        <dd>{number ?? '—'}</dd>{date && <small>{date}</small>}</div>
      {[[t('stats.matchdaysPlayed'), played], [t('stats.pendingMatchdays'), total - played],
        [t('stats.totalMatchdays'), total]].map(([label, count]) => <div key={label}>
        <dt>{label}</dt><dd>{total ? count.toLocaleString(i18n.language) : '—'}</dd></div>)}
    </dl>
    {total > 0 && <div className="rr-calendar-progress">
      <div><span>{t('modern.context.calendarProgress')}</span>
        <strong>{played} / {total}</strong></div>
      <div role="progressbar" aria-label={t('modern.context.calendarProgress')} aria-valuemin={0}
        aria-valuemax={total} aria-valuenow={played}><span style={{ width: `${percent}%` }} /></div>
      <p>{t('modern.context.calendarNote')}</p>
    </div>}
  </section>;
}

export function ReportRecentForm({ title, matches = [] }) {
  const { t } = useTranslation('reports');
  const titleId = useId();
  return <section className="rr-panel rr-recent-panel" aria-labelledby={titleId}>
    <header><span className="rr-eyebrow">{t('modern.context.form')}</span><h2 id={titleId}>{title}</h2>
      <p>{t('modern.recentSample', { count: matches.length })}</p></header>
    {matches.length ? <ol className="rr-recent-list">{matches.map((match, index) => {
      const outcome = ['win', 'draw', 'loss'].includes(match.outcome) ? match.outcome : null;
      const VenueIcon = match.home ? HomeOutlinedIcon : NorthEastIcon;
      return <li key={match.idPartido ?? match.id ?? index} className={`rr-recent-match rr-tone-${outcome}`}>
        <Avatar src={match.home ? match.escudoVisitante : match.escudoLocal} alt="">
          {match.opponent?.slice(0, 1)}</Avatar>
        <div className="rr-recent-opponent"><strong>{match.opponent}</strong>
          <span><VenueIcon aria-hidden="true" />{t(match.home ? 'match.home' : 'match.away')}
            {match.round != null && <> · {t('match.matchday', { n: match.round })}</>}</span></div>
        <strong className="rr-recent-score">{match.golesCasa} <span>–</span> {match.golesVisitante}</strong>
        <span className="rr-recent-outcome" aria-label={outcome ? t(`modern.context.${outcome}`) : t('match.pending')}
          title={outcome ? t(`modern.context.${outcome}`) : t('match.pending')}>
          {outcome ? t(`modern.outcomes.${outcome}`) : '—'}</span>
      </li>;
    })}</ol> : <p className="rr-empty">{t('empty.noRecentMatches')}</p>}
  </section>;
}

export function ReportContextStandings({ teams = [], selected }) {
  const { t, i18n } = useTranslation('reports');
  const titleId = useId();
  const fields = [['pj', 'jugados'], ['won', 'ganados'], ['drawn', 'empatados'], ['lost', 'perdidos'],
    ['gf', 'golesFavor'], ['gc', 'golesContra'], ['pts', 'puntos']];
  const format = value => reportNumber(value)?.toLocaleString(i18n.language) ?? '—';
  if (!teams.length) return null;
  return <section className="rr-panel rr-context-standings" aria-labelledby={titleId}>
    <header className="rr-performance-heading"><div><span className="rr-eyebrow">{t('modern.context.leaguePosition')}</span>
      <h2 id={titleId}>{t('sections.standings')}</h2></div><EmojiEventsOutlinedIcon aria-hidden="true" /></header>
    <div className="rr-data-table"><table aria-label={t('sections.standings')}>
      <thead><tr><th scope="col">{t('table.pos')}</th><th scope="col">{t('table.team')}</th>
        {fields.map(([label]) => <th scope="col" key={label}>{t(`table.${label}`)}</th>)}</tr></thead>
      <tbody>{teams.map((team, index) => <tr key={team.codequipo ?? index}
        className={team.nombre === selected ? 'rr-context-selected' : undefined}
        aria-current={team.nombre === selected ? 'true' : undefined}>
        <td><span className="rr-context-position">{format(team.posicion)}</span></td>
        <th scope="row"><div className="rr-context-team"><Avatar src={team.escudoEquipo} alt="">{team.nombre?.slice(0, 1)}</Avatar>
          <div><strong>{team.nombre}</strong>{team.nombre === selected && <small>{t('modern.selectedTeam')}</small>}</div>
        </div></th>
        {fields.map(([, field]) => <td key={field}>{format(team[field])}</td>)}
      </tr>)}</tbody>
    </table></div>
  </section>;
}
